import React, {useEffect, useState} from 'react'
//components
import { Button } from '@components/ui/buttons/Button'
import Label from '@components/ui/labels/Label'
import { Input } from '@components/ui/inputs/Input'
import { InputContainer } from "@components/ui/inputs/InputContainers";
import { APIMessage } from '@components/ui/messages/APIMessage';
import TimezoneSelector from '@components/ui/timezone/TimezoneSelector';
import { Toggle } from '@components/ui/inputs/Toggle';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
//api
import { createJobAPI, updateJobAPI } from '@services/jobsAPI'//api
import { createJobLinkAPI } from "@services/jobPermissionsAPI"
//contexts
import { useLoading } from '@contexts/LoadingContext'
import { useError } from '@contexts/ErrorContext';
import { useSuccess } from '@contexts/SuccessContext';
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext';
import { useUser } from '@contexts/UserContext';
import { timezoneMap } from '@components/ui/timezone/timezoneMappings';
import useConfirmDialog from '@hooks/useConfirmDialog';
/**
 * 
 * @param {Function} setSelectedJob 
 * @param {DropdownOption[]} jobNameOptions 
 * @param {Function} setJobNameOptions
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Job Control
 */
export const JobDetails = ({selectedJob, setSelectedJob, jobNameOptions, setJobNameOptions}) => {
    // set state

    const [jobNumber, setJobNumber] = useState('');
    const [jobName, setJobName] = useState('');
    const [client, setClient] = useState('');
    const [diameter, setDiameter] = useState('');
    const [thickness, setThickness] = useState('');
    const [timezone, setTimezone] = useState('');
    const [isLocked, setIsLocked] = useState(false);
    const [lineName, setLineName] = useState('');

    const [jobItemCounts, setJobItemCounts] = useState({
        AGMs: selectedJob?.AGMCount,
        POIs: selectedJob?.POICount,
        Subchains: selectedJob?.SubchainCount,
    }); //use counts to tell user if they are missing AGMs or Subchains, or if a locked job has POIs (meaning they can't unlock job)

    const toggleLocked = selectedJob ? (selectedJob.IsLocked && selectedJob.POICount > 0) : false

    const [validationErrors, setValidationErrors] = useState({});
    const { showLoading, hideLoading } = useLoading();
    const { showError } = useError();
    const { timeoutSuccess, confirmSuccess } = useSuccess();
    const { setHasUnsavedChanges } = useUnsavedChanges();
    const { setStoredJob } = useUser();
    const [ ConfirmDialog, requestConfirm ] = useConfirmDialog()

    //helper functions 

    const validate = async() => {
        const errors = {}

        //input already ensures that type is number, checking that it's filled in
        if(jobNumber.length === 0){
            errors.jobNumber = "Please enter job number"
        }
        if(jobName.length === 0){
            errors.jobName = "Please enter job name"
        }
        if(!timezone || timezone === ''){
            errors.timezone = "Please select a timezone"
        }

        //if errors were found, update validationErrors state and return false
        if(Object.keys(errors).length > 0){
            setValidationErrors(errors)
            return false
        }

        //if no errors, return true, set validationErrors state to empty
        setValidationErrors({})
        return true

    }

    const handleSubmit = async() =>{
        //show loading message
        showLoading("Submitting...");

        //validate
        const isValid = await validate();
        
        if(!isValid){
            hideLoading()
            return
        }

        //submit to api
        let jobData = {
            Job_Number: jobNumber.trim(),
            Job_Name: jobName.trim(),
            IsActive: false,
            Timezone: timezone,
            IsLocked: isLocked,
            Client: client,
            Diameter: diameter,
            Thickness: thickness,
            Line_Name: lineName,   
        };

        if(selectedJob && selectedJob.value){

            //prompt user to check site map before updating from unlocked -> locked
            const isLocking = selectedJob.IsLocked !== true && isLocked === true;

            if(isLocking){
                const isConfirmed = await requestConfirm("Please ensure you have checked Site Map for correctness, as changes to AGMs and Subchains are not possible once job is locked.", "Continue Saving", "Cancel", "success")

                if(!isConfirmed){
                    hideLoading();
                    return;
                } 
            }


            let response = await updateJobAPI({
                ...jobData,
                JobID: selectedJob.value
            })

            if(response.success){
                timeoutSuccess('Job Updated');
                const updatedJob = {
                    value: selectedJob.value,
                    label: `Job_${jobNumber}_${jobName}`,
                    timezone: timezoneMap[timezone],
                    IsLocked: isLocked,
                    Client: client,
                    Diameter: diameter,
                    Thickness: thickness,   
                    Line_Name: lineName,   
                    POICount: response.data.POICount,
                    AGMCount: response.data.AGMCount,
                    SubchainCount: response.data.SubchainCount,
                    RunCount: response.data.RunCount,
                    notificationCount: response.data.notificationCount
                }

                setSelectedJob(updatedJob)
                setStoredJob(updatedJob)
                setJobNameOptions(prev => {
                    const updatedOptions = [...prev].map(option => {
                        if(option.value === selectedJob.value){
                            return updatedJob
                        }
                        return option
                    })
                    return updatedOptions
                })
            } else {
                showError(response.message)

            }

        } else {
            let response = await createJobAPI(jobData);

            if(response.success){
                const data = response.data;

                const newJob = {
                    value: data.JobID,
                    label: `Job_${data.Job_Number}_${data.Job_Name}`,
                    timezone: timezoneMap[timezone],
                    IsLocked: isLocked,
                    Client: client,
                    Diameter: diameter,
                    Thickness: thickness,   
                    Line_Name: lineName,
                    POICount: 0,
                    AGMCount: 0,
                    SubchainCount: 0,
                    RunCount: 0,   
                    notificationCount: {}
                }

                setJobNameOptions([...jobNameOptions, newJob]);
                setSelectedJob(newJob);
                setHasUnsavedChanges(false);
                setStoredJob(newJob)

                //reset inputs
                setJobNumber("");
                setJobName("");
                timeoutSuccess('Job Created');
            } else {
                showError(response.message)
            }
        }



        hideLoading();
    }

        //create job link 
    
        const handleGetJobLink = async (JobID) => {
            showLoading("Getting job link...")

            const response = await createJobLinkAPI(JobID);

            hideLoading()

            if (!response.success) {
                showError(response.message)
                return
            }

            confirmSuccess(`Job link: ${response.link}`)
        }

    useEffect(() => {

        if(!selectedJob || selectedJob.length === 0){

            setJobName("")
            setJobNumber("")
            setClient("")
            setDiameter("")
            setThickness("")
            setLineName("")

            setTimezone("MST") //always default to MST
            setIsLocked(false)
            setJobItemCounts({
                AGMs: 0,
                POIs: 0,
                Subchains: 0,
            })
            return
        }

        const splitJobString = selectedJob.label?.split("_")
        
        const jobNumber = splitJobString?.[1] || ""
        const jobName = splitJobString?.[2] || ""

        setJobName(jobName)
        setJobNumber(jobNumber)
        setClient(selectedJob.Client || "")
        setDiameter(selectedJob.Diameter || "")
        setThickness(selectedJob.Thickness || "")

        setTimezone(selectedJob.timezone?.code)
        setIsLocked(selectedJob.IsLocked ?? false)
        setLineName(selectedJob.Line_Name || "")

        setJobItemCounts({
            AGMs: selectedJob.AGMCount,
            POIs: selectedJob.POICount,
            Subchains: selectedJob.SubchainCount,
        })

    }, [selectedJob])

    return (

        <div className="flex flex-col my-10">
            {/* create job link button */}
            {selectedJob ? (
                <div>
                    <Button 
                        onClick={() => handleGetJobLink(selectedJob.value)} variant="noBackground"
                        className="text-sm px-0 mb-10"
                    >
                        <i className="fa-solid fa-link mr-2 text-purple-800 "></i>
                        Get Job Link
                    </Button>
                </div>
            ) 
            :  null
            }

            {/* job number */}
            <InputContainer>
                <Label htmlFor="jobNumber">Job number</Label>
                <Input 
                    name="jobNumber" 
                    value={jobNumber} 
                    onChange={(e)=>setJobNumber(e.target.value)} 
                    error={validationErrors.jobNumber}
                />
                <APIMessage message={validationErrors.jobNumber} isError={true} className="my-1 italic text-sm"/>
            </InputContainer>
            
            
            {/* job name */}
            <InputContainer>
                <Label htmlFor='jobName'>Job name</Label>
                <Input 
                    name="jobName" 
                    value={jobName} 
                    onChange={(e)=>setJobName(e.target.value)} 
                    error={validationErrors.jobName}
                />
                <APIMessage message={validationErrors.jobName} isError={true} className="my-1 italic text-sm"/>
            </InputContainer>

            {/* client */}
            <InputContainer>
                <Label htmlFor='client'>Client</Label>
                <Input 
                    name="client" 
                    value={client} 
                    onChange={(e)=>setClient(e.target.value)} 
                />
            </InputContainer>
            
            {/* diameter */}
            <InputContainer>
                <Label htmlFor='diameter'>Diameter</Label>
                <Input 
                    name="diameter" 
                    value={diameter} 
                    onChange={(e)=>setDiameter(e.target.value)} 
                    type='number'
                />
            </InputContainer>            
            
            {/* thickness */}
            <InputContainer>
                <Label htmlFor='jobName'>Thickness</Label>
                <Input 
                    name="thickness" 
                    value={thickness} 
                    onChange={(e)=>setThickness(e.target.value)}
                    type='number'
                />
            </InputContainer>
            
            {/* line name */}
            <InputContainer>
                <Label htmlFor='jobName'>Line Name</Label>
                <Input 
                    name="lineName" 
                    value={lineName} 
                    onChange={(e)=>setLineName(e.target.value)}
                    type='string'
                />
            </InputContainer>

            {/* Timezone select */}
            <InputContainer>
                <Label htmlFor='timezone-selector'>Select timezone</Label>
                <TimezoneSelector 
                    onChange={(selectedOption) => setTimezone(selectedOption.value)}
                    error={validationErrors.timezone}
                    currentTimezone={timezone}
                />
                <APIMessage message={validationErrors.timezone} isError={true} className="my-1 italic text-sm"/>
            </InputContainer>

            {selectedJob && (
                <>
                
                <InputContainer>
                    <Label>
                        Job locked
                        <Tippy
                            content={
                            <div className="text-sm max-w-sm">
                                <p className="mb-2"> Locking the job prevents AGMs and Subchains from being added or
                                deleted, as well as their locations being changed.
                                </p>
                                <p className="mb-2">The job cannot be unlocked once POIs have been added, because POIs rely on upstream and downstream AGMs and Subchains to send accurate notifications.
                                </p>
                                <p>The job can be unlocked if POIs are deleted.</p>
                            </div>
                            }
                            placement="right"
                            interactive
                            delay={[200, 0]}
                        >
                            <i
                                className="fa-solid fa-circle-question text-violet-800 ml-2 cursor-help"
                                aria-label="What does locking a job mean?"
                            />
                        </Tippy>
                    </Label>
                    <Toggle
                        checked={isLocked}
                        id={`isLocked-toggle`}
                        labelText=""
                        onChange={(e) => setIsLocked(prev => !prev)}
                        locked={toggleLocked}
                        lockMessage={toggleLocked ?  "This job has POIs and cannot be unlocked." : null}
                    />


                </InputContainer>
                {
                    jobItemCounts.AGMs === 0 && (
                    <p className='italic text-sm text-amber-600'>
                        <i className="fa-solid fa-circle-exclamation mr-2" ></i>
                        This job has no AGMs
                    </p>
                )
                }
                {
                    jobItemCounts.Subchains === 0 && (
                    <p className='italic text-sm text-amber-600'>
                        <i className="fa-solid fa-circle-exclamation mr-2" ></i>
                        This job has no Subchains
                    </p>
                )
                }
                </>
            )}


            {/* submit button and error/save messages */}
            <Button variant="success" onClick={handleSubmit} className='w-max mt-10'>{selectedJob ? 'Update' : 'Create'}</Button>

            <APIMessage message={Object.keys(validationErrors).length > 0 ? "Some form fields are missing" : null} isError={true} />

            <ConfirmDialog />
        </div>
    )
}

